import { useState, useEffect, useCallback } from "react"
import { Plus, Edit, Trash2, Loader2, Save, X, Image as ImageIcon, Users } from "lucide-react"
import AdminSidebar from "../components/AdminSidebar"
import ConfirmModal from "../components/ConfirmModal"
import ViewRegistrantsModal from "../components/ViewRegistrantsModal"
import { useLanguage } from "../lib/LanguageContext"

const API = process.env.REACT_APP_AUTH_API_URL || "http://localhost:8001"

const AGE_GROUP_OPTIONS = ["Open", "U-19", "U-17", "U-15", "U-13"]
const CATEGORY_OPTIONS = ["Freestyle", "Backstroke", "Breaststroke", "Butterfly", "Medley", "Relay"]

// Stored as a comma-separated string (no schema change needed — every other
// reader of event.age_group/category just displays it as text, never parses
// it as a single exact value).
function toList(value) {
  return (value || "").split(",").map(s => s.trim()).filter(Boolean)
}
function toCsv(list) {
  return list.join(", ")
}

export default function AdminEvents() {
  const [events, setEvents] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState("")
  
  const [showModal, setShowModal] = useState(false)
  const [currentEvent, setCurrentEvent] = useState(null)
  const [formData, setFormData] = useState(initialFormState())
  const [formError, setFormError] = useState("")
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const [viewRegistrantsEvent, setViewRegistrantsEvent] = useState(null)
  const [isViewRegistrantsOpen, setIsViewRegistrantsOpen] = useState(false)
  const { t } = useLanguage()

  const [subEventName, setSubEventName] = useState("")
  const [subEventPrice, setSubEventPrice] = useState("")

  function handleAddSubEvent() {
    if (!subEventName.trim() || !subEventPrice) return
    const newSub = {
      id: Math.random().toString(36).substring(2, 9),
      name: subEventName.trim(),
      price: Number(subEventPrice)
    }
    setFormData(prev => ({
      ...prev,
      available_sub_events: [...(prev.available_sub_events || []), newSub]
    }))
    setSubEventName("")
    setSubEventPrice("")
  }

  function handleRemoveSubEvent(id) {
    setFormData(prev => ({
      ...prev,
      available_sub_events: prev.available_sub_events.filter(se => se.id !== id)
    }))
  }

  function openViewRegistrants(evt) {
    setViewRegistrantsEvent(evt)
    setIsViewRegistrantsOpen(true)
  }

  function initialFormState() {
    return {
      title: "",
      date: "",
      end_date: "",
      venue: "",
      age_group: ["Open"],
      entry_fee: "",
      category: ["Freestyle"],
      poster_color: "#29ABE2",
      poster_base64: null,
      available_sub_events: []
    }
  }

  function toggleFormListValue(field, value) {
    setFormData(prev => {
      const has = prev[field].includes(value)
      const next = has ? prev[field].filter(v => v !== value) : [...prev[field], value]
      return { ...prev, [field]: next }
    })
  }

  function authHeaders() {
    const token = localStorage.getItem("token")
    return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
  }

  const fetchEvents = useCallback(async () => {
    setIsLoading(true)
    try {
      const res = await fetch(`${API}/api/events`, { headers: authHeaders() })
      if (!res.ok) throw new Error("Failed to fetch events")
      setEvents(await res.json())
    } catch (e) {
      setError(e.message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchEvents()
  }, [fetchEvents])

  function openNew() {
    setCurrentEvent(null)
    setFormData(initialFormState())
    setFormError("")
    setSubEventName("")
    setSubEventPrice("")
    setShowModal(true)
  }

  function openEdit(evt) {
    setCurrentEvent(evt)
    setFormData({
      title: evt.title,
      date: evt.date,
      end_date: evt.end_date || "",
      venue: evt.venue,
      age_group: toList(evt.age_group),
      entry_fee: evt.entry_fee,
      category: toList(evt.category),
      poster_color: evt.poster_color,
      poster_base64: null, // null means don't change
      available_sub_events: evt.available_sub_events || []
    })
    setFormError("")
    setSubEventName("")
    setSubEventPrice("")
    setShowModal(true)
  }

  async function confirmDelete() {
    setDeleting(true)
    try {
      const res = await fetch(`${API}/api/admin/events/${deleteTarget.id}`, { method: "DELETE", headers: authHeaders() })
      if (!res.ok) throw new Error("Delete failed")
      setDeleteTarget(null)
      fetchEvents()
    } catch (e) {
      setError(e.message)
      setDeleteTarget(null)
    } finally {
      setDeleting(false)
    }
  }

  async function toggleRegistrations(id) {
    try {
      const res = await fetch(`${API}/api/admin/events/${id}/toggle-registrations`, {
        method: "PATCH",
        headers: authHeaders()
      })
      if (!res.ok) throw new Error("Failed to toggle registrations")
      fetchEvents()
    } catch (err) {
      setError(err.message)
    }
  }

  async function handleSave(e) {
    e.preventDefault()
    setFormError("")
    if (formData.age_group.length === 0) return setFormError("Select at least one age group")
    if (formData.category.length === 0) return setFormError("Select at least one category")
    setIsSaving(true)
    try {
      const url = currentEvent ? `${API}/api/admin/events/${currentEvent.id}` : `${API}/api/admin/events`
      const method = currentEvent ? "PUT" : "POST"

      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: JSON.stringify({ ...formData, entry_fee: Number(formData.entry_fee), age_group: toCsv(formData.age_group), category: toCsv(formData.category) })
      })
      if (!res.ok) throw new Error("Save failed")

      setShowModal(false)
      fetchEvents()
    } catch (err) {
      setFormError(err.message)
    } finally {
      setIsSaving(false)
    }
  }

  function handleFileChange(e) {
    const file = e.target.files[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) {
      setFormError("Image must be less than 5MB")
      e.target.value = ""
      return
    }
    const reader = new FileReader()
    reader.onload = (ev) => {
      setFormData(prev => ({ ...prev, poster_base64: ev.target.result }))
    }
    reader.readAsDataURL(file)
  }

  return (
    <div className="flex flex-col min-h-screen bg-ink-900 lg:flex-row">
      <AdminSidebar />
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-10">
        <header className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-paper-0">{t("admin_events_title")}</h1>
            <p className="mt-1 text-sm text-mist-300">{t("admin_events_subtitle")}</p>
          </div>
          <button onClick={openNew} className="flex items-center gap-2 rounded-lg bg-gold-500 px-4 py-2 text-sm font-bold text-ink-900 hover:bg-gold-400 transition-colors">
            <Plus className="h-4 w-4" /> {t("admin_events_new")}
          </button>
        </header>

        {error && <div className="mb-6 rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>}

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {isLoading ? (
            <div className="col-span-full flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-gold-400" /></div>
          ) : events.map(evt => (
            <div key={evt.id} className="flex flex-col overflow-hidden rounded-xl border border-white/10 bg-ink-800/30 transition-all hover:bg-ink-800/50">
              <div 
                className="h-32 w-full bg-cover bg-center flex items-end p-4 relative" 
                style={{ backgroundColor: evt.poster_color, backgroundImage: evt.poster_url ? `url(${evt.poster_url})` : "none" }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <span className="relative z-10 text-[10px] font-bold uppercase tracking-widest text-white/80">{evt.date.slice(0, 10)}</span>
              </div>
              <div className="flex flex-1 flex-col p-5">
                <h3 className="font-display text-xl font-bold text-paper-0">{evt.title}</h3>
                <p className="mt-1 text-sm text-mist-300 line-clamp-1">{evt.venue}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="rounded bg-white/5 px-2 py-1 text-[10px] font-semibold text-mist-400">{evt.category}</span>
                  <span className="rounded bg-white/5 px-2 py-1 text-[10px] font-semibold text-mist-400">{evt.age_group}</span>
                  <span className={`rounded px-2 py-1 text-[10px] font-semibold ${evt.status === 'live' ? 'bg-red-500/20 text-red-400' : evt.status === 'upcoming' ? 'bg-gold-500/20 text-gold-400' : 'bg-white/10 text-mist-500'}`}>
                    {evt.status.toUpperCase()}
                  </span>
                </div>
                <div className="mt-6 flex gap-2 border-t border-white/5 pt-4">
                  <button onClick={() => toggleRegistrations(evt.id)} className={`flex flex-[1.5] items-center justify-center gap-1.5 rounded py-2 text-xs font-semibold transition-colors ${evt.registrations_open ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20' : 'bg-white/5 text-mist-400 hover:bg-white/10'}`}>
                    {evt.registrations_open ? t("admin_events_reg_open") : t("admin_events_reg_closed")}
                  </button>
                  <button onClick={() => openEdit(evt)} className="flex flex-1 items-center justify-center gap-2 rounded bg-white/5 py-2 text-xs font-semibold text-mist-300 hover:bg-white/10 hover:text-paper-0">
                    <Edit className="h-3.5 w-3.5" /> {t("admin_events_edit")}
                  </button>
                  <button onClick={() => setDeleteTarget(evt)} className="flex items-center justify-center rounded bg-red-500/10 px-3 py-2 text-red-400 hover:bg-red-500/20">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                <button 
                  onClick={() => openViewRegistrants(evt)} 
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded bg-blue-500/10 py-2 text-xs font-bold text-blue-400 transition-colors hover:bg-blue-500/20"
                >
                  <Users className="h-4 w-4" /> {t("admin_events_view_regs")}
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4 backdrop-blur-sm overflow-y-auto">
          <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-ink-800 shadow-2xl my-8">
            <div className="flex items-center justify-between px-8 pt-8 pb-6 border-b border-white/10">
              <h2 className="font-display text-2xl font-bold text-paper-0">{currentEvent ? t("admin_events_modal_edit") : t("admin_events_modal_create")}</h2>
              <button onClick={() => setShowModal(false)} className="text-mist-400 hover:text-paper-0"><X className="h-5 w-5" /></button>
            </div>

            <form onSubmit={handleSave} className="flex flex-col gap-6 p-8">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {/* 1. Top-Left: Basic details */}
                <section className="flex h-full flex-col rounded-xl border border-white/5 bg-white/[0.02] p-5">
                  <h3 className="mb-4 text-xs font-bold uppercase tracking-widest text-gold-400">{t("admin_events_basic_details")}</h3>
                  <div className="flex flex-col gap-4">
                    <div>
                      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-mist-500">{t("admin_events_title_label")}</label>
                      <input required value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="e.g. National Championship" className="w-full rounded-lg border border-white/10 bg-ink-900/40 p-3.5 text-sm text-paper-0 placeholder:text-mist-600 focus:border-gold-500 focus:outline-none focus:ring-1 focus:ring-gold-500/30" />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-mist-500">{t("admin_events_venue")}</label>
                      <input required value={formData.venue} onChange={e => setFormData({...formData, venue: e.target.value})} placeholder="e.g. My Dinh Aquatics Center" className="w-full rounded-lg border border-white/10 bg-ink-900/40 p-3.5 text-sm text-paper-0 placeholder:text-mist-600 focus:border-gold-500 focus:outline-none focus:ring-1 focus:ring-gold-500/30" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-mist-500">{t("admin_events_start_date")}</label>
                        <input type="date" required value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} className="w-full rounded-lg border border-white/10 bg-ink-900/40 p-3.5 text-sm text-paper-0 focus:border-gold-500 focus:outline-none focus:ring-1 focus:ring-gold-500/30 [color-scheme:dark]" />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-mist-500">{t("admin_events_end_date")}</label>
                        <input type="date" value={formData.end_date || ""} onChange={e => setFormData({...formData, end_date: e.target.value})} className="w-full rounded-lg border border-white/10 bg-ink-900/40 p-3.5 text-sm text-paper-0 focus:border-gold-500 focus:outline-none focus:ring-1 focus:ring-gold-500/30 [color-scheme:dark]" />
                      </div>
                    </div>
                  </div>
                </section>

                {/* 2. Top-Right: Pricing & poster */}
                <section className="flex h-full flex-col rounded-xl border border-white/5 bg-white/[0.02] p-5">
                  <h3 className="mb-4 text-xs font-bold uppercase tracking-widest text-gold-400">{t("admin_events_pricing_poster")}</h3>
                  <div className="flex flex-col gap-4">
                    <div>
                      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-mist-500">{t("admin_events_base_fee")}</label>
                      <input type="number" required min="0" value={formData.entry_fee} onChange={e => setFormData({...formData, entry_fee: e.target.value === "" ? "" : Number(e.target.value)})} className="w-full rounded-lg border border-white/10 bg-ink-900/40 p-3.5 text-sm text-paper-0 focus:border-gold-500 focus:outline-none focus:ring-1 focus:ring-gold-500/30" />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-mist-500">{t("admin_events_poster")}</label>
                      {formData.poster_base64 !== "" && (formData.poster_base64 || (currentEvent && currentEvent.poster_url)) ? (
                        <div className="relative block h-32 w-full overflow-hidden rounded-lg border border-white/10 group">
                          <img src={formData.poster_base64 || currentEvent.poster_url} alt="Poster preview" className="h-full w-full object-cover opacity-70 transition-opacity group-hover:opacity-40" />
                          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/20 opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100">
                            <label className="flex cursor-pointer items-center gap-2 rounded-lg bg-black/60 px-3 py-1.5 text-xs font-semibold text-white hover:bg-black/80">
                              <ImageIcon className="h-3.5 w-3.5" /> {t("admin_events_change")}
                              <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                            </label>
                            <button type="button" onClick={() => setFormData({...formData, poster_base64: ""})} className="flex items-center gap-2 rounded-lg bg-red-500/80 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-500">
                              <Trash2 className="h-3.5 w-3.5" /> {t("admin_events_remove")}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <label className="flex h-16 w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-white/20 bg-ink-900/30 text-sm text-mist-400 hover:bg-white/5 hover:text-paper-0">
                          <ImageIcon className="h-4 w-4" /> {t("admin_events_upload_poster")}
                          <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                        </label>
                      )}
                      <div className="mt-3 flex items-center gap-3">
                        <input type="color" value={formData.poster_color} onChange={e => setFormData({...formData, poster_color: e.target.value})} style={{ width: 36, height: 36, padding: 0 }} className="shrink-0 cursor-pointer rounded border border-white/10 bg-ink-900/40" />
                        <p className="text-xs text-mist-500">{t("admin_events_fallback_color")}</p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* 3. Bottom-Left: Classification */}
                <section className="flex h-full flex-col rounded-xl border border-white/5 bg-white/[0.02] p-5">
                  <h3 className="mb-4 text-xs font-bold uppercase tracking-widest text-gold-400">{t("admin_events_classification")}</h3>
                  <div className="flex flex-col gap-4">
                    <div>
                      <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-mist-500">{t("admin_events_age_group")}</label>
                      <div className="flex flex-wrap gap-2">
                        {AGE_GROUP_OPTIONS.map(opt => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => toggleFormListValue("age_group", opt)}
                            aria-pressed={formData.age_group.includes(opt)}
                            className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${formData.age_group.includes(opt) ? "bg-gold-500 text-ink-900" : "bg-ink-900/40 text-mist-300 border border-white/10 hover:border-white/25"}`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-mist-500">{t("admin_events_main_cat")}</label>
                      <div className="flex flex-wrap gap-2">
                        {CATEGORY_OPTIONS.map(opt => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => toggleFormListValue("category", opt)}
                            aria-pressed={formData.category.includes(opt)}
                            className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${formData.category.includes(opt) ? "bg-gold-500 text-ink-900" : "bg-ink-900/40 text-mist-300 border border-white/10 hover:border-white/25"}`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </section>

                {/* 4. Bottom-Right: Events */}
                <section className="flex h-full flex-col rounded-xl border border-white/5 bg-white/[0.02] p-5">
                  <h3 className="mb-4 text-xs font-bold uppercase tracking-widest text-gold-400">{t("admin_events_events_list")}</h3>
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-3">
                      <div>
                        <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-mist-500">{t("admin_events_name")}</label>
                        <input value={subEventName} onChange={e => setSubEventName(e.target.value)} placeholder="e.g. 50m Freestyle" className="w-full rounded-lg border border-white/10 bg-ink-900/40 p-3.5 text-sm text-paper-0 placeholder:text-mist-600 focus:border-gold-500 focus:outline-none focus:ring-1 focus:ring-gold-500/30" />
                      </div>
                      <div className="flex items-end gap-2">
                        <div className="flex-1">
                          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-mist-500">{t("admin_events_price")}</label>
                          <input type="number" min="0" value={subEventPrice} onChange={e => setSubEventPrice(e.target.value)} placeholder="0" className="w-full rounded-lg border border-white/10 bg-ink-900/40 p-3.5 text-sm text-paper-0 placeholder:text-mist-600 focus:border-gold-500 focus:outline-none focus:ring-1 focus:ring-gold-500/30" />
                        </div>
                        <button type="button" onClick={handleAddSubEvent} className="mb-px flex h-[50px] items-center justify-center rounded-lg bg-white/10 px-6 text-sm font-bold text-mist-300 transition-colors hover:bg-white/20 hover:text-paper-0">
                          {t("admin_events_add")}
                        </button>
                      </div>
                    </div>
                    {formData.available_sub_events && formData.available_sub_events.length > 0 && (
                      <div className="mt-2 flex max-h-48 flex-col gap-2 overflow-y-auto pr-2">
                        {formData.available_sub_events.map((se) => (
                          <div key={se.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-ink-900/50 p-3">
                            <span className="text-sm font-medium text-paper-0">{se.name}</span>
                            <div className="flex items-center gap-4">
                              <span className="text-sm font-semibold text-gold-400">${se.price}</span>
                              <button type="button" onClick={() => handleRemoveSubEvent(se.id)} className="text-mist-500 hover:text-red-400">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </section>
              </div>

              {formError && <p className="text-sm font-medium text-red-300">{formError}</p>}

              <div className="flex justify-end gap-3 pt-2 border-t border-white/10">
                <button type="button" onClick={() => setShowModal(false)} className="rounded-lg px-4 py-2.5 text-sm font-semibold text-mist-300 hover:text-paper-0">{t("common_cancel")}</button>
                <button type="submit" disabled={isSaving} className="flex items-center gap-2 rounded-lg bg-gold-500 px-6 py-2.5 text-sm font-bold text-ink-900 hover:bg-gold-400 disabled:opacity-50">
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {t("admin_events_save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!deleteTarget}
        title={t("admin_events_delete_title")}
        message={deleteTarget ? `${t("admin_events_delete_msg1")}${deleteTarget.title}${t("admin_events_delete_msg2")}` : ""}
        confirmLabel={t("admin_events_delete_btn")}
        busy={deleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

      <ViewRegistrantsModal 
        open={isViewRegistrantsOpen} 
        onClose={() => setIsViewRegistrantsOpen(false)} 
        event={viewRegistrantsEvent} 
      />
    </div>
  )
}
